import refreshTokenClient from 'api/refreshTokenClient';
import { useAuthStore } from 'stores/authStore';
import { getTokenPayload, payloadExpires } from 'utils/auth';

/** @type {Promise<string>|null} */
let refreshPromise = null;

const getAccessToken = () => useAuthStore.getState().accessToken;

const accessExpiresWithin = (accessToken, expiresWithinSeconds) => {
  if (!accessToken) return true;
  try {
    const payload = getTokenPayload(accessToken);
    const expiresAt = payloadExpires(payload);
    const refreshAt = expiresAt.clone().subtract(expiresWithinSeconds, 'seconds');
    return !expiresAt.isValid() || refreshAt.isSameOrBefore(undefined);
  } catch {
    return true;
  }
};

/**
 * Cookie-based refresh via refreshTokenClient. Single-flight: concurrent callers share one request.
 * @returns {Promise<string>} new access token
 */
export const refreshAccessToken = async () => {
  if (refreshPromise !== null) {
    return refreshPromise;
  }

  refreshPromise = refreshTokenClient
    .post('/v1/auth/refresh', {})
    .then((response) => {
      const { access } = response.data;
      useAuthStore.getState().setAccessToken(access);
      return access;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
};

/**
 * Refresh when access is missing or expires within the given window.
 * @param {{ expiresWithinSeconds?: number }} [options]
 * @returns {Promise<string|null>} current or refreshed access token
 */
export const refreshIfExpiring = async ({ expiresWithinSeconds = 60 } = {}) => {
  const accessToken = getAccessToken();
  if (!accessExpiresWithin(accessToken, expiresWithinSeconds)) {
    return accessToken;
  }

  return refreshAccessToken();
};
